import pb from '../api/pocketbase';

/**
 * Service pour la gestion des dépenses
 * Gère toutes les opérations CRUD sur les différentes collections de dépenses
 */

// ==================== CRÉATION DE DÉPENSES ====================

/**
 * Crée une nouvelle demande de dépense
 * @param {Object} data - Les données de la dépense
 * @returns {Promise<Object>} La dépense créée
 */
export async function createDepense(data) {
  try {
    const formData = new FormData();

    // Champs obligatoires
    formData.append('nom', data.nom || '');
    formData.append('nomProjet', data.nomProjet || data.nom || '');
    formData.append('description', data.description || '');
    formData.append('chefChantier', data.chefChantier || '');
    formData.append('date', data.date || new Date().toISOString());
    formData.append('montant', Number(data.montant) || 0);
    formData.append('status', data.status || 'en_attente');
    formData.append('createdBy', data.createdBy || '');
    formData.append('fournisseur', data.fournisseur || '');
    formData.append('rubrique', data.rubrique || '');
    formData.append('sousRubrique', data.sousRubrique || '');

    // Métadonnées
    formData.append('dateCreation', new Date().toISOString());
    formData.append('creePar', 'chef_chantier');

    // Fichier justificatif
    if (data.justificatifFile instanceof File) {
      formData.append('justificatif', data.justificatifFile);
    }

    // Rubriques (format JSON)
    if (data.rubriques && Array.isArray(data.rubriques)) {
      formData.append('rubriques', JSON.stringify(data.rubriques));
    }

    const record = await pb.collection('demandesDepenses').create(formData);
    console.log('✅ Dépense créée avec succès:', record.id);
    return record;
  } catch (err) {
    console.error("❌ Erreur création dépense:", err.message, err.data);
    throw new Error(`Erreur lors de la création de la dépense: ${err.message}`);
  }
}

/**
 * Crée une dépense mise en attente dans une collection spécifique
 * @param {Object} data - Les données de la dépense
 * @param {string} collection - La collection cible (depensemeaRT, depensemeaCG, depensemeaDG)
 * @returns {Promise<Object>} La dépense MEA créée
 */
export async function createDepenseMEA(data, collection = 'depensemeaRT') {
  try {
    const formData = new FormData();

    // Champs de base
    formData.append('nomProjet', data.nomProjet || '');
    formData.append('chefChantier', data.chefChantier || '');
    formData.append('rubrique', data.rubrique || '');
    formData.append('sousRubrique', data.sousRubrique || '');
    formData.append('montant', Number(data.montant) || 0);
    formData.append('fournisseur', data.fournisseur || '');
    formData.append('description', data.description || '');
    formData.append('motif', data.motif || '');
    formData.append('status', 'mise_en_attente');

    // Métadonnées MEA
    formData.append('datemea', new Date().toISOString());
    formData.append('misEnAttentePar', data.misEnAttentePar || 'responsable_technique');

    // Fichier justificatif
    if (data.justificatifFile instanceof File) {
      formData.append('justificatif', data.justificatifFile);
    }

    // ID de la dépense originale
    if (data.depenseOriginaleId) {
      formData.append('depenseOriginaleId', data.depenseOriginaleId);
    }

    const record = await pb.collection(collection).create(formData);
    console.log(`✅ Dépense MEA créée dans ${collection}:`, record.id);
    return record;
  } catch (err) {
    console.error(`❌ Erreur création dépense MEA dans ${collection}:`, err.message);
    throw new Error(`Erreur lors de la création de la dépense MEA: ${err.message}`);
  }
}

// ==================== LECTURE DE DÉPENSES ====================

/**
 * Récupère toutes les demandes de dépenses
 * @param {Object} filters - Filtres de recherche
 * @returns {Promise<Array>} Liste des dépenses
 */
export async function getDemandesDepenses(filters = {}) {
  try {
    let filterString = '';
    const filterParts = [];

    if (filters.nomProjet) {
      filterParts.push(`nomProjet="${filters.nomProjet}"`);
    }
    if (filters.chefChantier) {
      filterParts.push(`chefChantier="${filters.chefChantier}"`);
    }
    if (filters.status) {
      filterParts.push(`status="${filters.status}"`);
    }
    if (filters.dateDebut) {
      filterParts.push(`date>="${filters.dateDebut}"`);
    }
    if (filters.dateFin) {
      filterParts.push(`date<="${filters.dateFin}"`);
    }

    if (filterParts.length > 0) {
      filterString = filterParts.join(' && ');
    }

    const options = {
      sort: '-date',
      ...(filterString && { filter: filterString })
    };

    const records = await pb.collection('demandesDepenses').getFullList(options);
    return records;
  } catch (err) {
    console.error("❌ Erreur récupération demandes dépenses:", err.message);
    throw new Error(`Erreur lors de la récupération des dépenses: ${err.message}`);
  }
}

/**
 * Récupère les dépenses mises en attente depuis plusieurs collections
 * @param {string} nomProjet - Nom du projet
 * @param {Array} collections - Collections à interroger
 * @returns {Promise<Array>} Liste des dépenses MEA
 */
export async function getDepensesMEA(nomProjet, collections = ['depensemeaRT', 'depensemeaCG', 'depensemeaDG']) {
  try {
    let allDepenses = [];

    for (const collection of collections) {
      try {
        const depenses = await pb.collection(collection).getFullList({
          filter: `nomProjet="${nomProjet}"`,
          sort: '-datemea'
        });
        
        // Ajouter la source de collection
        const depensesWithSource = depenses.map(depense => ({
          ...depense,
          sourceCollection: collection
        }));
        
        allDepenses = [...allDepenses, ...depensesWithSource];
      } catch (err) {
        console.warn(`⚠️ Erreur collection ${collection}:`, err.message);
      }
    }

    // Trier par date de mise en attente
    allDepenses.sort((a, b) => new Date(b.datemea || b.created) - new Date(a.datemea || a.created));
    
    return allDepenses;
  } catch (err) {
    console.error("❌ Erreur récupération dépenses MEA:", err.message);
    throw new Error(`Erreur lors de la récupération des dépenses MEA: ${err.message}`);
  }
}

/**
 * Récupère les dépenses validées par le DG
 * @param {string} nomProjet - Nom du projet
 * @returns {Promise<Array>} Liste des dépenses validées
 */
export async function getDepensesValideesDG(nomProjet) {
  try {
    const records = await pb.collection('depensesValideesDG').getFullList({
      filter: `nomProjet="${nomProjet}"`,
      sort: '-dateValidation'
    });
    return records;
  } catch (err) {
    console.error("❌ Erreur récupération dépenses validées DG:", err.message);
    throw new Error(`Erreur lors de la récupération des dépenses validées: ${err.message}`);
  }
}

// ==================== MISE À JOUR DE DÉPENSES ====================

/**
 * Met à jour une demande de dépense
 * @param {string} depenseId - ID de la dépense
 * @param {Object} data - Nouvelles données
 * @returns {Promise<Object>} La dépense mise à jour
 */
export async function updateDepense(depenseId, data) {
  try {
    const formData = new FormData();

    // Ajouter tous les champs de mise à jour
    Object.keys(data).forEach(key => {
      if (key !== 'justificatifFile' && data[key] !== undefined) {
        if (typeof data[key] === 'object' && data[key] !== null) {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    // Métadonnées de modification
    formData.append('dateModification', new Date().toISOString());
    formData.append('modifiePar', data.modifiePar || 'unknown');

    // Fichier justificatif
    if (data.justificatifFile instanceof File) {
      formData.append('justificatif', data.justificatifFile);
    }

    const record = await pb.collection('demandesDepenses').update(depenseId, formData);
    console.log('✅ Dépense mise à jour:', record.id);
    return record;
  } catch (err) {
    console.error("❌ Erreur mise à jour dépense:", err.message);
    throw new Error(`Erreur lors de la mise à jour de la dépense: ${err.message}`);
  }
}

/**
 * Met à jour une dépense mise en attente
 * @param {string} id - ID de la dépense MEA
 * @param {Object} data - Nouvelles données
 * @param {string} collection - Collection source
 * @returns {Promise<Object>} La dépense MEA mise à jour
 */
export const updateDepenseMEA = async (id, data, collection = 'depensemeaRT') => {
  try {
    const updateData = { ...data };

    // Ajouter les métadonnées de modification
    if (!updateData.dateModification) {
      updateData.dateModification = new Date().toISOString();
    }
    if (!updateData.modifiePar) {
      updateData.modifiePar = 'chef_chantier';
    }

    // Si c'est un renvoi, ajouter les métadonnées spécifiques
    if (data.status === 'en_attente') {
      updateData.dateRenvoi = new Date().toISOString();
      updateData.renvoyePar = updateData.modifiePar;
    }

    // Gestion des fichiers
    if (data.justificatifFile instanceof File) {
      const formData = new FormData();
      Object.keys(updateData).forEach(key => {
        if (key !== 'justificatifFile' && updateData[key] !== undefined) {
          if (typeof updateData[key] === 'object' && updateData[key] !== null) {
            formData.append(key, JSON.stringify(updateData[key]));
          } else {
            formData.append(key, updateData[key]);
          }
        }
      });
      formData.append('justificatif', data.justificatifFile);
      
      const record = await pb.collection(collection).update(id, formData);
      console.log(`✅ Dépense MEA mise à jour avec fichier dans ${collection}:`, record.id);
      return record;
    } else {
      const record = await pb.collection(collection).update(id, updateData);
      console.log(`✅ Dépense MEA mise à jour dans ${collection}:`, record.id);
      return record;
    }
  } catch (err) {
    console.error(`❌ Erreur mise à jour dépense MEA dans ${collection}:`, err.message);
    throw new Error(`Erreur lors de la mise à jour de la dépense MEA: ${err.message}`);
  }
};

// ==================== SUPPRESSION DE DÉPENSES ====================

/**
 * Supprime une demande de dépense
 * @param {string} depenseId - ID de la dépense à supprimer
 * @returns {Promise<void>}
 */
export async function supprimerDepense(depenseId) {
  try {
    await pb.collection('demandesDepenses').delete(depenseId);
    console.log('✅ Dépense supprimée:', depenseId);
  } catch (err) {
    console.error("❌ Erreur suppression dépense:", err.message);
    throw new Error(`Erreur lors de la suppression de la dépense: ${err.message}`);
  }
}

/**
 * Supprime une dépense MEA
 * @param {string} id - ID de la dépense MEA
 * @param {string} collection - Collection source
 * @returns {Promise<void>}
 */
export async function supprimerDepenseMEA(id, collection = 'depensemeaRT') {
  try {
    await pb.collection(collection).delete(id);
    console.log(`✅ Dépense MEA supprimée de ${collection}:`, id);
  } catch (err) {
    console.error(`❌ Erreur suppression dépense MEA de ${collection}:`, err.message);
    throw new Error(`Erreur lors de la suppression de la dépense MEA: ${err.message}`);
  }
}

// ==================== VALIDATION DE DÉPENSES ====================

/**
 * Valide une dépense et la déplace vers les dépenses validées
 * @param {Object} depense - La dépense à valider
 * @param {string} validePar - Qui valide la dépense
 * @returns {Promise<Object>} La dépense validée
 */
export async function validerDepense(depense, validePar = 'directeur_general') {
  try {
    const formData = new FormData();

    // Copier toutes les données de la dépense originale
    Object.keys(depense).forEach(key => {
      if (!['id', 'created', 'updated', 'collectionId', 'collectionName'].includes(key)) {
        if (typeof depense[key] === 'object' && depense[key] !== null) {
          formData.append(key, JSON.stringify(depense[key]));
        } else if (depense[key] !== undefined) {
          formData.append(key, depense[key]);
        }
      }
    });

    // Métadonnées de validation
    formData.append('dateValidation', new Date().toISOString());
    formData.append('validePar', validePar);
    formData.append('status', 'validee');
    formData.append('depenseOriginaleId', depense.id);

    const record = await pb.collection('depensesValideesDG').create(formData);
    console.log('✅ Dépense validée et créée:', record.id);
    return record;
  } catch (err) {
    console.error("❌ Erreur validation dépense:", err.message);
    throw new Error(`Erreur lors de la validation de la dépense: ${err.message}`);
  }
}

// ==================== UTILITAIRES ====================

/**
 * Calcule le total des dépenses selon des critères
 * @param {Array} depenses - Liste des dépenses
 * @param {Object} criteres - Critères de filtrage
 * @returns {number} Total calculé
 */
export function calculerTotalDepenses(depenses, criteres = {}) {
  try {
    let depensesFiltrees = [...depenses];

    if (criteres.rubrique) {
      depensesFiltrees = depensesFiltrees.filter(d => d.rubrique === criteres.rubrique);
    }

    if (criteres.status) {
      depensesFiltrees = depensesFiltrees.filter(d => d.status === criteres.status);
    }

    if (criteres.dateDebut) {
      const dateDebut = new Date(criteres.dateDebut);
      depensesFiltrees = depensesFiltrees.filter(d => new Date(d.date || d.created) >= dateDebut);
    }

    if (criteres.dateFin) {
      const dateFin = new Date(criteres.dateFin);
      depensesFiltrees = depensesFiltrees.filter(d => new Date(d.date || d.created) <= dateFin);
    }

    return depensesFiltrees.reduce((total, depense) => total + (Number(depense.montant) || 0), 0);
  } catch (err) {
    console.error("❌ Erreur calcul total dépenses:", err.message);
    return 0;
  }
}

/**
 * Groupe les dépenses par rubrique
 * @param {Array} depenses - Liste des dépenses
 * @returns {Object} Dépenses groupées par rubrique
 */
export function grouperDepensesParRubrique(depenses) {
  try {
    return depenses.reduce((groupes, depense) => {
      const rubrique = depense.rubrique || 'Non classé';
      if (!groupes[rubrique]) {
        groupes[rubrique] = [];
      }
      groupes[rubrique].push(depense);
      return groupes;
    }, {});
  } catch (err) {
    console.error("❌ Erreur groupement dépenses:", err.message);
    return {};
  }
}

/**
 * Génère un rapport de dépenses
 * @param {string} nomProjet - Nom du projet
 * @param {Object} options - Options du rapport
 * @returns {Promise<Object>} Rapport généré
 */
export async function genererRapportDepenses(nomProjet, options = {}) {
  try {
    const [demandes, depensesMEA, depensesValidees] = await Promise.all([
      getDemandesDepenses({ nomProjet }),
      getDepensesMEA(nomProjet),
      getDepensesValideesDG(nomProjet)
    ]);

    const rapport = {
      projet: nomProjet,
      dateGeneration: new Date().toISOString(),
      statistiques: {
        totalDemandes: demandes.length,
        totalMEA: depensesMEA.length,
        totalValidees: depensesValidees.length,
        montantTotalDemandes: calculerTotalDepenses(demandes),
        montantTotalMEA: calculerTotalDepenses(depensesMEA),
        montantTotalValidees: calculerTotalDepenses(depensesValidees)
      },
      depensesParRubrique: {
        demandes: grouperDepensesParRubrique(demandes),
        mea: grouperDepensesParRubrique(depensesMEA),
        validees: grouperDepensesParRubrique(depensesValidees)
      },
      donnees: {
        demandes,
        depensesMEA,
        depensesValidees
      }
    };

    console.log('✅ Rapport généré pour le projet:', nomProjet);
    return rapport;
  } catch (err) {
    console.error("❌ Erreur génération rapport:", err.message);
    throw new Error(`Erreur lors de la génération du rapport: ${err.message}`);
  }
}

// ==================== SUBSCRIPTIONS TEMPS RÉEL ====================

/**
 * S'abonne aux changements en temps réel sur une collection
 * @param {string} collection - Nom de la collection
 * @param {Function} callback - Fonction de rappel
 * @param {Object} filters - Filtres optionnels
 * @returns {Promise<Function>} Fonction de désabonnement
 */
export async function subscribeToCollection(collection, callback, filters = {}) {
  // Vérifier l'authentification avant de souscrire
  if (!pb.authStore.isValid) {
    console.debug(`Sub ${collection} skipped: not authenticated`);
    return () => {}; // Retourner une fonction vide
  }

  try {
    const subscription = await pb.collection(collection).subscribe('*', (e) => {
      // Appliquer les filtres si nécessaire
      let shouldTrigger = true;
      
      if (filters.nomProjet && e.record.nomProjet !== filters.nomProjet) {
        shouldTrigger = false;
      }
      
      if (shouldTrigger && typeof callback === 'function') {
        callback(e);
      }
    });

    console.log(`✅ Abonnement créé pour la collection: ${collection}`);
    
    // Retourner la fonction de désabonnement
    return () => {
      pb.collection(collection).unsubscribe(subscription);
      console.log(`✅ Désabonnement de la collection: ${collection}`);
    };
  } catch (err) {
    console.debug(`Sub ${collection} skipped:`, err?.message);
    return () => {}; // Retourner une fonction vide en cas d'erreur
  }
}

// export default {
//   // Création
//   createDepense,
//   createDepenseMEA,
  
//   // Lecture
//   getDemandesDepenses,
//   getDepensesMEA,
//   getDepensesValideesDG,
  
//   // Mise à jour
//   updateDepense,
//   updateDepenseMEA,
  
//   // Suppression
//   supprimerDepense,
//   supprimerDepenseMEA,
  
//   // Validation
//   validerDepense,
  
//   // Utilitaires
//   calculerTotalDepenses,
//   grouperDepensesParRubrique,
//   genererRapportDepenses,
  
//   // Temps réel
//   subscribeToCollection
// };